import type {
  TextBlock,
  PageAttributes,
  SubscriberAttributes,
  PageAttribute,
  Block,
} from "./types";

import { BLOCK_TYPES, NODE_TYPES } from "./blocks";
import {
  slotAttributeSlug,
  isSlotAttribute,
  parseSlotAttributeSlug,
} from "./slot-attributes";
import { isMultiLine, replaceTextInHtml } from "./text-utils";

export { slotAttributeSlug, parseSlotAttributeSlug } from "./slot-attributes";

export const TYPES = {
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
  STRING: "string",
  RICH_TEXT: "rich_text",
} as const;

export const BLOCK_PAGE_ATTRIBUTES = {
  IMAGE_PAGE_ATTRIBUTE: "image_page_attribute_slug",
  VIDEO_PAGE_ATTRIBUTE: "video_page_attribute_slug",
  AUDIO_PAGE_ATTRIBUTE: "audio_page_attribute_slug",
  PAGE_ATTRIBUTE: "page_attribute_slug",
} as const;

export const BUILT_IN_PAGE_ATTRIBUTES = {
  TITLE: "page.title",
  FIRST_NAME: "member.first_name",
} as const;

export const ATTRIBUTE_SCOPES = {
  BUILT_IN: "built_in",
  PAGE: "page",
} as const;

const BUILT_IN_PAGE_ATTRIBUTE_VALUES = Object.values(
  BUILT_IN_PAGE_ATTRIBUTES
) as string[];
const BLOCK_PAGE_ATTRIBUTE_VALUES = Object.values(
  BLOCK_PAGE_ATTRIBUTES
) as string[];

export const attributeScope = (attribute: PageAttribute): string => {
  if (BUILT_IN_PAGE_ATTRIBUTE_VALUES.includes(attribute.slug)) {
    return ATTRIBUTE_SCOPES.BUILT_IN;
  }

  return ATTRIBUTE_SCOPES.PAGE;
};

export const findAttributeBySlotSlug = (
  pageAttributes: PageAttributes = {},
  slotSlug: string | null | undefined
): PageAttribute | undefined => {
  if (!slotSlug) return undefined;

  return Object.values(pageAttributes).find(
    (attribute) => attribute?.slot_slug === slotSlug
  );
};

export const findAttribute = (
  pageAttributes: PageAttributes = {},
  options: { slug?: string | null; slotSlug?: string | null }
): PageAttribute | undefined => {
  if (options.slug && pageAttributes[options.slug]) {
    return pageAttributes[options.slug];
  }

  return findAttributeBySlotSlug(pageAttributes, options.slotSlug);
};

export const getTextContent = (
  textBlock: TextBlock,
  pageAttributes: PageAttributes = {},
  subscriberAttributes: SubscriberAttributes = {}
): string => {
  const { content, page_attribute_slug } = textBlock.data;
  const attribute = findAttribute(pageAttributes, {
    slug: page_attribute_slug,
    slotSlug: textBlock.uuid
      ? slotAttributeSlug(textBlock.uuid, "content")
      : null,
  });

  // If block doesn't have a resolved attribute, then setup for inline attribute
  if (!attribute) {
    return setInlineAttributeForContent(
      content,
      pageAttributes,
      subscriberAttributes
    );
  }

  const replacedValue = getValueForAttribute(attribute, subscriberAttributes);

  if (!replacedValue) return content;

  if (attribute.type === TYPES.STRING) {
    return replaceTextInHtml(content, replacedValue);
  } else if (attribute.type === TYPES.RICH_TEXT) {
    return replacedValue.startsWith("<")
      ? replacedValue
      : `<p>${replacedValue}</p>`;
  }

  return content;
};

export const setInlineAttributeForContent = (
  content: string,
  pageAttributes: PageAttributes,
  subscriberAttributes: SubscriberAttributes
): string => {
  try {
    const body = stringToHtml(content);

    if (!body) return content;

    body
      .querySelectorAll(`[data-type="${NODE_TYPES.DATA_FIELD}"]`)
      .forEach((e: any) => {
        const attribute = findAttribute(pageAttributes, {
          slug: e.dataset.slug,
          slotSlug: e.dataset.slug,
        });

        e.innerHTML = getValueForAttribute(
          attribute,
          subscriberAttributes
        );
      });

    return body.innerHTML;
  } catch {
    return content;
  }
};

const stringToHtml = (content: string): Element | null => {
  try {
    return new DOMParser()
      .parseFromString(content, "text/html")
      .documentElement.querySelector("body");
  } catch {
    return null;
  }
};

export const getValueForAttribute = (
  attribute: PageAttribute | null | undefined,
  subscriberAttributes: SubscriberAttributes
): string => {
  return attribute && isMemberAttribute(attribute)
    ? subscriberAttributes[attribute.slug] || attribute.value
    : attribute?.value || "";
};

export const isMemberAttribute = (
  attribute: PageAttribute | string | null | undefined
): boolean =>
  attribute
    ? (typeof attribute === "object" && "slug" in attribute
        ? attribute.slug
        : attribute
      ).startsWith("member.")
    : false;

export const parseToAttributeValue = (
  text: string | null,
  pageAttribute: PageAttribute
): string => {
  if (!text) return "";

  if (pageAttribute.type === TYPES.STRING) {
    return (
      new DOMParser()
        .parseFromString(text, "text/html")
        .documentElement?.querySelector("body")?.childNodes[0]?.textContent ||
      ""
    );
  } else if (pageAttribute.type === TYPES.RICH_TEXT) {
    return text.startsWith("<") ? text : `<p>${text}</p>`;
  } else {
    return "";
  }
};

const customAttributeSlugs = (
  pageAttributes: PageAttributes = {}
): Set<string> =>
  new Set(
    Object.values(pageAttributes)
      .filter(
        (attribute) =>
          !!attribute?.slug &&
          !attribute?.slot_slug &&
          !BUILT_IN_PAGE_ATTRIBUTE_VALUES.includes(attribute.slug) &&
          !isMemberAttribute(attribute)
      )
      .map((attribute) => attribute.slug)
  );

const hasMappedInlineAttribute = (
  textContent: string,
  attributeSlugs: Set<string>
): boolean => {
  const body = stringToHtml(textContent);
  if (!body) return false;

  return !!Array.from(
    body.querySelectorAll(`[data-type="${NODE_TYPES.DATA_FIELD}"]`)
  ).find((node: any) => {
    const slug = node.dataset.slug;
    return attributeSlugs.has(slug);
  });
};

const hasMappedAttribute = (
  block: Pick<Block, "data">,
  attributeSlugs: Set<string>
): boolean => {
  if (!block?.data) return false;
  const data = block.data as Record<string, any>;

  if (
    BLOCK_PAGE_ATTRIBUTE_VALUES.some((key) => {
      const slug = data[key];
      return typeof slug === "string" && attributeSlugs.has(slug);
    })
  ) {
    return true;
  }

  return (
    typeof data.content === "string" &&
    hasMappedInlineAttribute(data.content, attributeSlugs)
  );
};

export const hasDataFields = (
  pageAttributes: PageAttributes = {},
  blocks: Array<Pick<Block, "data">> = []
): boolean => {
  if (
    Object.values(pageAttributes).some((attribute) => !!attribute?.slot_slug)
  ) {
    return true;
  }

  const customAttributes = customAttributeSlugs(pageAttributes);
  if (!customAttributes.size) return false;

  return blocks.some((block) => hasMappedAttribute(block, customAttributes));
};

export const getDefaultAttributeType = (block: Block) => {
  switch (block.type) {
    case BLOCK_TYPES.TEXT_BLOCK:
      return isMultiLine(block.data.content) ? TYPES.RICH_TEXT : TYPES.STRING;
    case BLOCK_TYPES.HERO_BLOCK:
      return TYPES.IMAGE;
    default:
      return TYPES.STRING;
  }
};

export const isContentOnlyChange = (
  blockType: string,
  changedKeys: string[]
): boolean => {
  if (changedKeys.length === 0) return false;

  return changedKeys.every((key) => isSlotAttribute(blockType, key));
};

export const attributeIcon = (attributeType: string): string => {
  switch (attributeType) {
    case TYPES.STRING:
      return "format_size";

    case TYPES.RICH_TEXT:
      return "notes";

    case TYPES.IMAGE:
      return "image";

    case TYPES.VIDEO:
      return "slideshow";

    case TYPES.AUDIO:
      return "headphones";

    default:
      return "";
  }
};
